import React, { useState } from 'react';
import { Form, Input, Button, Alert, Space, Card, Typography, Radio, Divider, Row, Col, Checkbox } from 'antd';
import { UserOutlined, PhoneOutlined, MailOutlined, LockOutlined, CheckCircleOutlined, SearchOutlined, LogoutOutlined } from '@ant-design/icons';
import { publicApi } from '../../services/apiInstances';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import CustomerLogin from '../customer/CustomerLogin';
import AccountLinking from '../customer/AccountLinking';
import './BookingPortal.css';

const { Text, Title } = Typography;

interface BookingCustomerAuthProps {
  onAuthComplete: (customerData: any) => void;
  onGuestContinue: (customerInfo: { first_name: string; last_name: string; email: string; phone: string; password?: string }) => void;
}

type AuthState = 'idle' | 'checking' | 'has_account' | 'can_link' | 'email_exists' | 'new' | 'authenticated';

const BookingCustomerAuth: React.FC<BookingCustomerAuthProps> = ({ onAuthComplete, onGuestContinue }) => {
  const [form] = Form.useForm();
  const { isAuthenticated, customerData, login } = useCustomerAuth();
  const [authState, setAuthState] = useState<AuthState>(isAuthenticated ? 'authenticated' : 'idle');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [existingCustomers, setExistingCustomers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Account creation states
  const [wantsAccount, setWantsAccount] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const checkCustomerStatus = async (phone: string) => {
    if (!phone || phone.length < 10) return;

    setLoading(true);
    setError(null);

    try {
      const response = await publicApi.post(`/public/appointments/check-customer`, {
        phone,
      });

      const status = response.data.status;

      if (status === 'has_account') {
        setAuthState('has_account');
      } else if (status === 'can_link') {
        setExistingCustomers(response.data.customers || []);
        setAuthState('can_link');
      } else if (status === 'email_exists') {
        setError(response.data.message || 'This email is already in use with a different phone number.');
        setAuthState('email_exists');
      } else {
        setAuthState('new');
      }
    } catch (err: any) {
      console.error('Error checking customer status:', err);
      setError('Failed to check customer information. Please try again.');
      setAuthState('new'); // Default to new customer on error
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneSearch = () => {
    if (phoneNumber && phoneNumber.length >= 10) {
      checkCustomerStatus(phoneNumber);
    }
  };

  const handleReset = () => {
    setAuthState('idle');
    setPhoneNumber('');
    setExistingCustomers([]);
    setError(null);
    form.resetFields();
  };

  const handleLoginSuccess = () => {
    setAuthState('authenticated');
    if (customerData?.customer) {
      onAuthComplete(customerData.customer);
    }
  };

  const handleLinkingSuccess = () => {
    setAuthState('authenticated');
    if (customerData?.customer) {
      onAuthComplete(customerData.customer);
    }
  };

  const handleGuestSubmit = async (values: any) => {
    // Validate passwords if account creation is selected
    if (wantsAccount) {
      if (!password || password.length < 8) {
        setError('Password must be at least 8 characters');
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }
    }

    onGuestContinue({
      first_name: values.first_name,
      last_name: values.last_name,
      email: values.email || '', // Email is optional now
      phone: phoneNumber,
      password: wantsAccount ? password : undefined,
    });
  };

  // Already authenticated
  if (isAuthenticated && customerData?.customer) {
    return (
      <Alert
        message="Logged In"
        description={`Welcome back, ${customerData.customer.first_name}! Your information is ready.`}
        type="success"
        showIcon
        icon={<CheckCircleOutlined />}
        action={
          <Button
            size="small"
            icon={<LogoutOutlined />}
            onClick={() => {
              // Clear customer auth context
              window.location.reload();
            }}
          >
            Logout
          </Button>
        }
      />
    );
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      {/* Phone Number Input - Always Visible */}
      <Card>
        <Form.Item
          label="Phone Number"
          required
          help="Enter your phone number and click 'Check Account' or press Enter"
        >
          <div className="phone-input-container">
            <Space.Compact style={{ width: '100%' }}>
              <Input
                size="large"
                prefix={<PhoneOutlined />}
                placeholder="(555) 123-4567"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                onPressEnter={handlePhoneSearch}
                disabled={authState === 'authenticated' || loading}
                style={{ width: '100%' }}
              />
              <Button
                type="primary"
                size="large"
                icon={<SearchOutlined />}
                onClick={handlePhoneSearch}
                loading={loading}
                disabled={!phoneNumber || phoneNumber.length < 10 || authState === 'authenticated'}
              >
                Check Account
              </Button>
            </Space.Compact>
          </div>
        </Form.Item>

        {authState !== 'idle' && authState !== 'checking' && (
          <Button
            block
            icon={<LogoutOutlined />}
            onClick={handleReset}
            style={{ marginTop: 8 }}
          >
            Start Over / Use Different Phone
          </Button>
        )}
      </Card>

      {loading && (
        <Alert message="Checking your account..." type="info" showIcon />
      )}

      {error && (
        <Alert
          message="Notice"
          description={error}
          type="warning"
          showIcon
          closable
          onClose={() => setError(null)}
        />
      )}

      {/* Scenario A: Has Account - Show Login */}
      {authState === 'has_account' && (
        <Card>
          <Alert
            message="Welcome Back!"
            description="We found your account. Please login to continue with your booking."
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <CustomerLogin
            identifier={phoneNumber}
            onSuccess={handleLoginSuccess}
            onCancel={handleReset}
          />
        </Card>
      )}

      {/* Scenario B: Can Link - Offer Account Creation */}
      {authState === 'can_link' && (
        <Card>
          <Alert
            message="Service History Found!"
            description="We found your previous visits. Create an account to access your service history and manage your vehicles."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div>
              <Text strong>Your Service History:</Text>
              <ul style={{ marginTop: 8 }}>
                {existingCustomers.map((customer) => (
                  <li key={customer.id}>
                    <Text>{customer.name}</Text>
                    <Text type="secondary" style={{ marginLeft: 8 }}>
                      - {customer.vehicleCount} vehicle(s)
                      {customer.lastServiceDate && `, last service: ${new Date(customer.lastServiceDate).toLocaleDateString()}`}
                    </Text>
                  </li>
                ))}
              </ul>
            </div>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <AccountLinking
                  phone={phoneNumber}
                  customers={existingCustomers}
                  onSuccess={handleLinkingSuccess}
                  onCancel={handleReset}
                />
              </Col>
              <Col xs={24} sm={12}>
                <Button
                  block
                  size="large"
                  onClick={() => setAuthState('new')}
                  style={{ height: 'auto', padding: '20px' }}
                >
                  <Space direction="vertical" size={4}>
                    <Text strong>Continue as Guest</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Book without creating an account
                    </Text>
                  </Space>
                </Button>
              </Col>
            </Row>
          </Space>
        </Card>
      )}

      {/* Scenario C/D: New Customer - Show Full Form */}
      {(authState === 'new' || authState === 'email_exists') && (
        <Card title="Your Information">
          <Form form={form} layout="vertical" onFinish={handleGuestSubmit}>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="First Name"
                  name="first_name"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <Input size="large" prefix={<UserOutlined />} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Last Name"
                  name="last_name"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <Input size="large" prefix={<UserOutlined />} />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  label="Email"
                  name="email"
                  rules={[
                    // Required per spec D10, matching the server schema on
                    // POST /api/quote-requests/public. The label used to read
                    // "Email (Optional)" while the schema demanded it, then the
                    // schema was loosened to match the label; the spec settles it
                    // the other way. Label, rule and schema move together — a
                    // form and a schema that disagree about optionality is the
                    // defect class that took three passes to fix on 2026-07-29.
                    { required: true, type: 'email', message: 'We need an email address to send your quote' }
                  ]}
                >
                  <Input size="large" prefix={<MailOutlined />} placeholder="your@email.com" />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            {/* Account Creation Section */}
            <Alert
              message="Track Your Repairs Online"
              description="Create an account to view service history, manage appointments, and access your estimates anytime."
              type="info"
              showIcon
              icon={<UserOutlined />}
              style={{ marginBottom: 16 }}
            />

            <Form.Item>
              <Checkbox
                checked={wantsAccount}
                onChange={(e) => {
                  setWantsAccount(e.target.checked);
                  if (!e.target.checked) {
                    setPassword('');
                    setConfirmPassword('');
                    setError(null);
                  }
                }}
              >
                <Text strong>Create an account to login after repairs</Text>
              </Checkbox>
            </Form.Item>

            {wantsAccount && (
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Password"
                    help="Minimum 8 characters"
                    validateStatus={password && password.length < 8 ? 'error' : ''}
                  >
                    <Input.Password
                      size="large"
                      prefix={<LockOutlined />}
                      placeholder="Enter password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setError(null);
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Confirm Password"
                    validateStatus={confirmPassword && password !== confirmPassword ? 'error' : ''}
                    help={confirmPassword && password !== confirmPassword ? 'Passwords do not match' : ''}
                  >
                    <Input.Password
                      size="large"
                      prefix={<LockOutlined />}
                      placeholder="Confirm password"
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        setError(null);
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}

            <Form.Item>
              <Button type="primary" htmlType="submit" size="large" block>
                Continue
              </Button>
            </Form.Item>
          </Form>
        </Card>
      )}
    </Space>
  );
};

export default BookingCustomerAuth;
